// @flow

import React, { Component } from 'react';
import styled, { css } from 'styled-components';

import FormGroup from './FormGroup';
import Input, { Text } from './Input';

const FormGroupWrapper = styled(FormGroup)`
  margin-bottom: 1em;

  input, textarea {
    outline: none;
  }

  label {
    color: ${props => props.theme.input.placeholder};
    display: block;
    font-weight: ${props => props.required ? 'bold' : 'normal'};
    left: 0;
    line-height: 1.15;
    padding: 0 1em;
    pointer-events: none;
    position: absolute;
    top: 1em;
    transition: 100ms linear all;

    .labeled-input-required {
      display: none;
    }

    ${props => props.required && css`
      font-weight: bold;
      .labeled-input-required {
        display: inline;
      }
    `}

    ${props => (props.value.toString().length > 0 || props.isFocused) && css`
      background-color: white;
      color: ${props => props.theme.text.dark};
      font-size: 0.85em;
      left: 1em;
      padding: 0 0.5em;
      top: -0.5em;
    `}
  }

  ${props => props.error && css`
    label, input, textarea {
      border-color: ${props.theme.input.error.border};
      color: ${props.theme.input.error.color};
    }
  `}
`;

type Props = {
  autofocus: boolean,
  autocomplete?: string,
  error?: string,
  name: string,
  onChange?: (key: string, value: string) => void,
  placeholder: string,
  required: boolean,
  rows?: number,
  type: string,
  value: number | string
};

type State = {|
  isFocused: boolean
|};

type InputElement = HTMLInputElement | HTMLTextAreaElement;
type InputEvent = SyntheticEvent<InputElement>;

class LabeledInput extends Component<Props, State> {

  inputControl: InputElement

  state = {
    isFocused: false
  }

  static defaultProps = {
    autofocus: false,
    required: false,
    type: 'text'
  }

  componentDidMount = () => {
    if (this.props.autofocus) {
      this.inputControl.focus();
    }
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (this.state.isFocused && prevProps.value !== this.props.value) {
      if (this.props.value.toString().length === 0) {
        this.setState({ isFocused: false });
      }
    }
  }

  onBlurHandler = (event: InputEvent) => {
    if (!this.inputControl.value.length) {
      this.setState({ isFocused: false });
    }
  }

  onChangeHandler = (event: InputEvent) => {
    const value = event.currentTarget.value;
    const { name: key, onChange } = this.props;

    onChange && onChange(key, value);
  }

  onFocusHandler = (event: InputEvent) => {
    this.setState({ isFocused: true });
  }

  render() {
    const Component = this.props.type === 'textarea' ? Text : Input;
    const error = this.props.error && ` ${this.props.error}`;
    const required = this.props.required && '*';
    return (
      <FormGroupWrapper
        error={this.props.error}
        isFocused={this.state.isFocused}
        required={this.props.required}
        value={this.props.value}
      >
        <label>
          <span className="labled-input-placeholder">{this.props.placeholder}</span>
          <span className="labeled-input-error">{error}</span>
          <span className="labled-input-required">{required}</span>
        </label>

        <Component
          autoFocus={this.props.autofocus}
          autoComplete={this.props.autocomplete}
          innerRef={ref => this.inputControl = ref}
          onChange={this.onChangeHandler}
          onFocus={this.onFocusHandler}
          onBlur={this.onBlurHandler}
          required={this.props.required}
          rows={this.props.rows}
          type={this.props.type}
          value={this.props.value}
        />
      </FormGroupWrapper>
    );
  }

}

export default LabeledInput;



// WEBPACK FOOTER //
// ./src/components/LabeledInput.js